import { db } from '@/firebaseInit'
import _ from 'lodash'
import Vue from 'vue'
import MatchHelper from '@/lib/MatchHelper'
import DateHelper from '@/lib/DateHelper'
import uuidv4 from 'uuid/v4'
import MatchProcessHelper from '@/lib/MatchProcessHelper'

const entityPlural = 'matches'
const entitySingular = 'match'

const debug = require('debug')(`OD::Lib:Data:${_.startCase(_.toLower(entityPlural))}`)

function findCurrentUserName(vm, user) {
  let clubUser = null
  if (vm.clubUsers) {
    clubUser = vm.clubUsers.find(item => item.id === user.uid)
  }
  return clubUser && clubUser.customName
    ? clubUser.customName
    : user.displayName
    ? user.displayName
    : ''
}

async function addTmpValueToWeek(clubId, id, isNew) {
  if (isNew) {
    return db
      .collection(`clubs/${clubId}/matches`)
      .doc(id)
      .set({ tmp: 'forBackupEnable' })
  } else {
    return db
      .collection(`clubs/${clubId}/matches`)
      .doc(id)
      .update({ tmp: 'forBackupEnable' })
  }
}

async function createMatchesWeekDocForBackup(year, week, clubId) {
  const id = `${year}_${week}`
  const doc = await db
    .collection(`clubs/${clubId}/matches`)
    .doc(id)
    .get()

  if (doc.exists) {
    const data = doc.data()
    if (!('tmp' in data)) {
      return addTmpValueToWeek(clubId, id, false)
    }
  } else {
    return addTmpValueToWeek(clubId, id, true)
  }
}

function oppositeTeam(team) {
  return team === 'l' ? 'v' : 'l'
}

async function addMatch({ dbRef, club, sport, newMatch }) {
  debug('--- PROCESS ADD', { club, sport, newMatch })
  const doc = await dbRef.add(newMatch)
  return updateMatch({ dbRef: dbRef.doc(doc.id), club, sport, match: newMatch, dataToUpdate: {} })
}

async function updateMatch({ dbRef, club, sport, match, dataToUpdate }) {
  debug('updateMatch', { club, sport, match, dataToUpdate })
  const newMatch = { ...match, ...dataToUpdate }
  const matchProcessed = MatchProcessHelper.process({ club, sport, match: newMatch })
  const cache = JSON.parse(JSON.stringify(matchProcessed))
  if (cache) dataToUpdate.cache = cache
  debug('cache', { dataToUpdate, cache: dataToUpdate.cache })
  return dbRef.update(dataToUpdate)
}

export default {
  bindAll(vm, { clubId, year, week }, multiple) {
    debug(vm.$vnode.tag, 'bindAll', { clubId, year, week }, multiple)

    const id = `${year}_${week}`
    const collectionId = multiple ? `${entityPlural}_${clubId}` : entityPlural
    Vue.set(vm.loadings, collectionId, true)
    vm.$bind(collectionId, db.collection(`clubs/${clubId}/matches/${id}/${entityPlural}`))
      .then(items => {
        debug(vm.$vnode.tag, collectionId, items)
        Vue.set(vm.loadings, collectionId, false)
      })
      .catch(error => {
        debug(vm.$vnode.tag, collectionId, 'error', error)
        Vue.set(vm.loadings, collectionId, false)
        Vue.set(vm.dbErrors, collectionId, error)
      })
  },
  bindItem(vm, { clubId, year, week, matchId }) {
    debug(vm.$vnode.tag, 'bindItem', { clubId, year, week, matchId })

    const id = `${year}_${week}`
    Vue.set(vm.loadings, entitySingular, true)
    vm.$bind(
      entitySingular,
      db.collection(`clubs/${clubId}/matches/${id}/${entityPlural}`).doc(matchId)
    )
      .then(item => {
        debug(vm.$vnode.tag, entitySingular, item)
        Vue.set(vm.loadings, entitySingular, false)
      })
      .catch(error => {
        debug(vm.$vnode.tag, entitySingular, 'error', error)
        Vue.set(vm.loadings, entitySingular, false)
        Vue.set(vm.dbErrors, entitySingular, error)
      })
  },
  async add(vm, user, item, club, sport, { clubId, year, week }) {
    debug('add', { item })
    item.clubId = clubId
    item.year = year
    item.week = week
    item.sets = []
    let numSet = 0
    const config = MatchHelper.config(sport)
    switch (config.type) {
      case 'pointBase':
        numSet = config.maxNumSets
        break
      case 'timeBase':
        numSet = config.numPeriods
        break
      case 'baseballBase':
        numSet = config.minNumInnings
        break
      default:
        debug(`config.type not found [${config.type}]`)
        break
    }
    for (let index = 0; index < numSet; index++) {
      item.sets.push({ l: 0, v: 0 })
    }
    item.created = Date.now()
    item.createdBy = user ? user.uid : null
    debug(vm.$vnode.tag, 'add', { user, item, clubId, year, week }, config)
    debug('firestoreRefs', vm.$firestoreRefs)
    await addMatch({ dbRef: vm.$firestoreRefs[entityPlural], club, sport, newMatch: item })
    // If a Document doesn't have field the collections are not saved in backup process
    await createMatchesWeekDocForBackup(year, week, clubId)
  },
  async update(vm, user, club, sport, item, newItem) {
    debug(vm.$vnode.tag, 'update', { user, club, sport, newItem, item })
    const dbRef = vm.$firestoreRefs[entityPlural]
      ? vm.$firestoreRefs[entityPlural].doc(item.id)
      : vm.$firestoreRefs[entitySingular]
    await updateMatch({ dbRef, club, sport, match: item, dataToUpdate: newItem })
  },
  async delete(vm, user, item) {
    debug(vm.$vnode.tag, 'delete', { user, item })
    const dbRef = vm.$firestoreRefs[entityPlural]
      ? vm.$firestoreRefs[entityPlural].doc(item.id)
      : vm.$firestoreRefs[entitySingular]
    await dbRef.delete()
  },
  proccessEvent(vm, { match, user, data }) {
    debug('proccessEvent', {
      match,
      user,
      data,
    })
    const sets = match.sets
    const set = sets[data.set]
    if (data.num > 0) {
      let teamAux = data.team
      if (data.opposite) {
        teamAux = oppositeTeam(teamAux)
      }
      set[teamAux] = set[teamAux] + parseInt(data.num)
    }

    if (set.events) {
      if (data.action === 'downtime') {
        const downtimeBySet = MatchHelper.getMaxNumDowntimesBySet(data.config)
        const numDowntimes = set.events.filter(
          event => event.action === 'downtime' && event.team === data.team
        ).length
        if (numDowntimes >= downtimeBySet) {
          return null
        }
      }

      if (data.action === 'out') {
        const max = 3
        const num = set.events.filter(event => event.action === 'out' && event.team === data.team)
          .length
        if (num >= max) {
          return null
        }
      }

      if (data.action === '3outs') {
        const max = 1
        const num = set.events.filter(event => event.action === '3outs' && event.team === data.team)
          .length
        if (num >= max) {
          return null
        }
      }
    }

    if (!set.events) {
      set.events = []
    }

    /*
    // Clean set
    sets[set] = {
      l: 0,
      v: 0,
      events: [],
    }
    */
    const lastEventSendBy = findCurrentUserName(vm, user)
    set.events.push({
      action: data.action,
      team: data.team,
      num: data.num || 0,
      by: lastEventSendBy,
      when: DateHelper.getTodayLongString(),
      text: data.text || '',
      description: data.description || '',
      color: data.color || '',
      code: data.code || '',
      opposite: data.opposite || false,
      player: data.player || {
        num: '?',
      },
      extraPlayer: data.extraPlayer || {
        num: '',
      },
      hide: data.eventHide ? data.eventHide : false,
      linked: data.linked || false,
    })

    // Add extra periods if needed
    debug('Check Add extra sets', {
      config: data.config,
      needExtraPeriodsOnDraw: data.config.needExtraPeriodsOnDraw,
      maxNumExtraPeriods: data.config.maxNumExtraPeriods,
      match,
    })
    if (data.config.needExtraPeriodsOnDraw) {
      const result = MatchHelper.getMatchResult(data.config, match)
      const finished = MatchHelper.isMatchFinish(data.config, match, result)
      if (finished) {
        if (result.l === result.v) {
          if (data.config.maxNumExtraPeriods === 0) {
            // add sets until not draw
            match.sets.push({ l: 0, v: 0 })
            debug('Set Added')
          } else {
            // Add only one time sets
            if (data.config.numPeriods === match.sets.length) {
              for (let i = 0; i < data.config.maxNumExtraPeriods; i++) {
                match.sets.push({ l: 0, v: 0 })
                debug('Set Added')
              }
            }
          }
        }
      }
    }

    if (data.config.type === 'baseballBase') {
      if (data.set >= data.config.minNumInnings - 1) {
        // Add extra inning if needed
        if (data.action === 'out' || data.action === '3outs') {
          const numOuts = set.events.filter(event => event.action === 'out').length
          const num3Outs = set.events.filter(event => event.action === '3outs').length
          if (numOuts >= 6 || num3Outs >= 2) {
            let localResult = 0
            let visitorResult = 0
            if (match.sets) {
              match.sets.map((set, i) => {
                localResult += set.l
                visitorResult += set.v
              })
            }
            if (localResult === visitorResult) {
              match.sets.push({ l: 0, v: 0 })
            }
          }
        }
      }

      const maxStrikes = 3
      const maxBalls = 4

      let numStrikes = 0
      let numBalls = 0

      set.events.forEach(event => {
        if (data.team === event.team) {
          switch (event.action) {
            case 'strike':
              numStrikes += 1
              break
            case 'ball':
              numBalls += 1
              break
          }
        }
      })
      if (data.action === 'strike') {
        if (numStrikes % maxStrikes === 0) {
          // Add out
          const dataOut = data.config.actionControls.find(control => control.action === 'out')
          set.events.push({
            action: dataOut.action,
            team: data.team,
            num: dataOut.num || 0,
            by: lastEventSendBy,
            when: DateHelper.getTodayLongString(),
            text: dataOut.text || '',
            description: dataOut.description || '',
            color: dataOut.color || '',
            code: dataOut.code || '',
            opposite: dataOut.opposite || false,
            player: data.player || {
              num: '?',
            },
            extraPlayer: data.extraPlayer || {
              num: '',
            },
            hide: dataOut.eventHide ? dataOut.eventHide : false,
            linked: true,
          })
          // Add strokeout
          const dataStrokeout = data.config.actionControls.find(
            control => control.action === 'strokeout'
          )
          set.events.push({
            action: dataStrokeout.action,
            team: data.team,
            num: dataStrokeout.num || 0,
            by: lastEventSendBy,
            when: DateHelper.getTodayLongString(),
            text: dataStrokeout.text || '',
            description: dataStrokeout.description || '',
            color: dataStrokeout.color || '',
            code: dataStrokeout.code || '',
            opposite: dataStrokeout.opposite || false,
            player: data.player || {
              num: '?',
            },
            extraPlayer: data.extraPlayer || {
              num: '',
            },
            hide: dataStrokeout.eventHide ? dataStrokeout.eventHide : false,
            linked: true,
          })
        }
      }
      if (data.action === 'ball') {
        if (numBalls % maxBalls === 0) {
          // Add basebyball
          const dataBaseByBall = data.config.actionControls.find(
            control => control.action === 'basebyball'
          )
          set.events.push({
            action: dataBaseByBall.action,
            team: data.team,
            num: dataBaseByBall.num || 0,
            by: lastEventSendBy,
            when: DateHelper.getTodayLongString(),
            text: dataBaseByBall.text || '',
            description: dataBaseByBall.description || '',
            color: dataBaseByBall.color || '',
            code: dataBaseByBall.code || '',
            opposite: dataBaseByBall.opposite || false,
            player: data.player || {
              num: '?',
            },
            extraPlayer: data.extraPlayer || {
              num: '',
            },
            hide: dataBaseByBall.eventHide ? dataBaseByBall.eventHide : false,
            linked: true,
          })
        }
      }
    }

    return { sets, lastEventSendBy }
  },
  removeEvent(vm, { match, user, set, config }) {
    debug('removeEvent', { match, user, set, config })
    const sets = match.sets
    if (sets[set].events && sets[set].events.length > 0) {
      const lastEvent = sets[set].events.pop()
      if (lastEvent.num > 0) {
        let team = lastEvent.team
        if (lastEvent.opposite) {
          team = oppositeTeam(team)
        }
        if (sets[set][team] > 0) {
          sets[set][team] = sets[set][team] - (parseInt(lastEvent.num) || 1)
        } else {
          sets[set][team] = 0
        }
      }
      const lastEventSendBy = findCurrentUserName(vm, user)
      if (lastEvent.linked) {
        return this.removeEvent(vm, { match, user, set, config })
      }
      // Remove extra periods if needed
      debug('Check Remove sets', {
        action: lastEvent.action,
        needExtraPeriodsOnDraw: config.needExtraPeriodsOnDraw,
        numPeriods: config.numPeriods,
        set: set + 1,
        maxNumExtraPeriods: config.maxNumExtraPeriods,
      })
      if (lastEvent.action === 'stop') {
        if (config.needExtraPeriodsOnDraw) {
          if (config.numPeriods <= set + 1) {
            if (config.maxNumExtraPeriods === 0) {
              debug('Set Removed')
              sets.splice(-1, 1)
            } else {
              const currentSet = set + 1
              if (config.numPeriods === currentSet) {
                const num = match.sets.length - config.numPeriods
                for (let i = 0; i < num; i++) {
                  sets.splice(-1, 1)
                  debug('Set Removed')
                }
              }
            }
          }
        }
      }
      return { sets, lastEventSendBy }
    }
    return null
  },
  async addPlayer(vm, user, club, sport, data) {
    debug(vm.$vnode.tag, 'addPlayer', { user, club, sport, data })
    const match = vm.matches ? vm.matches.find(item => item.id === data.matchId) : vm.match
    if (match) {
      const sets = match.sets
      if (!sets[data.set].players) {
        sets[data.set].players = { l: [], v: [] }
      }

      let players = sets[data.set].players[data.team]
      if (players.find(item => item.num === data.player.num)) {
        players = _.remove(players, item => item.num === data.player.num)
      } else {
        const num = players.length
        if (num < data.config.numPlayerInSet + data.config.numExtraPlayerInSet) {
          players.push(data.player)
        }
      }
      players.sort((a, b) => parseInt(a.num) - parseInt(b.num))

      debug('WWWW', sets)
      const dbRef = vm.$firestoreRefs[entityPlural]
        ? vm.$firestoreRefs[entityPlural].doc(data.matchId)
        : vm.$firestoreRefs[entitySingular]
      await updateMatch({ dbRef, club, sport, match, dataToUpdate: { sets } })
    }
  },
  async sendEvent(vm, user, club, sport, data) {
    debug(vm.$vnode.tag, 'sendEvent', { user, club, sport, data })
    const match = vm.matches ? vm.matches.find(item => item.id === data.matchId) : vm.match
    if (match) {
      debug('AAAAA', match.id)
      // if (!match.id && data.matchId) match.id = data.matchId
      // if (!match.id && data.matchId) match.id = data.matchId
      debug('BBBBB', match.id)
      const processedData = this.proccessEvent(vm, { match, user, data })
      if (processedData) {
        const dbRef = vm.$firestoreRefs[entityPlural]
          ? vm.$firestoreRefs[entityPlural].doc(data.matchId)
          : vm.$firestoreRefs[entitySingular]
        debug('CCCCC', match.id, match)
        await updateMatch({ dbRef, club, sport, match, dataToUpdate: processedData })
      }
    }
  },
  async removeLastEvent(vm, user, club, sport, { config, matchId, set }) {
    debug(vm.$vnode.tag, 'removeLastEvent', { user, club, sport, config, matchId, set })
    const match = vm.matches ? vm.matches.find(item => item.id === matchId) : vm.match
    // const sport = vm.sport
    if (match) {
      const processedData = this.removeEvent(vm, { match, user, set, config })
      if (processedData) {
        const dbRef = vm.$firestoreRefs[entityPlural]
          ? vm.$firestoreRefs[entityPlural].doc(matchId)
          : vm.$firestoreRefs[entitySingular]

        await updateMatch({ dbRef, club, sport, match, dataToUpdate: processedData })
      }
    }
  },
  async toogleBlockMatch(vm, user, club, sport, { matchId }) {
    debug(vm.$vnode.tag, 'toogleBlockMatch', { user, club, sport, matchId })
    const match = vm.matches ? vm.matches.find(item => item.id === matchId) : vm.match
    const blocked = match.blocked ? !match.blocked : true
    const dbRef = vm.$firestoreRefs[entityPlural]
      ? vm.$firestoreRefs[entityPlural].doc(matchId)
      : vm.$firestoreRefs[entitySingular]
    await updateMatch({ dbRef, club, sport, match, dataToUpdate: { blocked } })
  },
  async addComment(vm, user, club, sport, { matchId, text }) {
    debug(vm.$vnode.tag, 'addComment', { user, club, sport, matchId, text })
    const match = vm.matches ? vm.matches.find(item => item.id === matchId) : vm.match
    if (match) {
      let comments = match.comments

      if (!comments) {
        comments = []
      }
      const lastEventSendBy = findCurrentUserName(vm, user)
      comments.push({
        id: uuidv4(),
        text,
        by: lastEventSendBy,
        when: DateHelper.getTodayLongString(),
      })

      const dbRef = vm.$firestoreRefs[entityPlural]
        ? vm.$firestoreRefs[entityPlural].doc(matchId)
        : vm.$firestoreRefs[entitySingular]
      await updateMatch({ dbRef, club, sport, match, dataToUpdate: { comments } })
    }
  },
  async removeComment(vm, user, club, sport, { matchId, commentId }) {
    debug(vm.$vnode.tag, 'removeComment', { user, club, sport, matchId, commentId })
    const match = vm.matches ? vm.matches.find(item => item.id === matchId) : vm.match
    if (match) {
      if (match.comments) {
        const comments = match.comments.filter(comment => comment.id !== commentId)
        const dbRef = vm.$firestoreRefs[entityPlural]
          ? vm.$firestoreRefs[entityPlural].doc(matchId)
          : vm.$firestoreRefs[entitySingular]
        await updateMatch({ dbRef, club, sport, match, dataToUpdate: { comments } })
      }
    }
  },
}
